import {
  DC_Repositories_Base_Enumerations_BusinessUnit,
  DC_Repositories_Base_Enumerations_Label,
  RequestModels_Financials_AdvancePaymentNlRequestModel,
  RequestModels_Financials_FinancialPreferencesRequest,
  RequestModels_Financials_PaymentArrangementsRequest,
  RequestModels_Financials_PutDesiredAdvancePaymentRequest,
  ResponseDataDC_Domain_Models_Accounts_PaymentPlanBreakdown_PaymentPlanBreakdown,
  ResponseModels_Financials_AdvancePaymentAdvice,
  ResponseModels_Financials_AdvancePaymentAdviceV2_AdvancePaymentAdviceV2,
  ResponseModels_Financials_AdvancePaymentResponseModelBe,
  ResponseModels_Financials_AdvancePaymentResponseModelNl,
  ResponseModels_Financials_FinancialOverviewResponse,
  ResponseModels_Financials_FinancialPreferencesResponse,
  ResponseModels_Financials_Invoices_InvoiceOverviewResponseModel,
  ResponseModels_Financials_PaymentArrangement_PaymentArrangementModel,
  ResponseModels_Financials_PaymentArrangement_PaymentArrangementResponseModel,
} from '@monorepo-types/dc';

import { request } from '../client';
import type { ApiRequestConfig } from '../client/types';

type GetAdvancePaymentNl = {
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  accountId: number;
  uniquePaymentParts?: boolean;
};
/**
 * Get the advance payment amount details for NL.
 *
 * @returns ResponseModels_Financials_AdvancePaymentResponseModelNl Success
 */
export function getAdvancePaymentNl(
  { label, customerId, accountId, uniquePaymentParts }: GetAdvancePaymentNl,
  requestConfig: ApiRequestConfig = {},
): Promise<ResponseModels_Financials_AdvancePaymentResponseModelNl> {
  return request(
    {
      method: 'GET',
      path: `/dxpweb/nl/${label}/customers/${customerId}/accounts/${accountId}/advancepayment`,
      query: { uniquePaymentParts },
      errors: { 400: 'Bad Request' },
    },
    requestConfig,
  );
}

type PutAdvancePaymentNl = {
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  accountId: number;
  requestBody?: RequestModels_Financials_AdvancePaymentNlRequestModel;
};
/**
 * Update advance payment amount
 *
 * @returns void
 */
export function putAdvancePaymentNl(
  { label, customerId, accountId, requestBody }: PutAdvancePaymentNl,
  requestConfig: ApiRequestConfig = {},
): Promise<void> {
  return request(
    {
      method: 'PUT',
      path: `/dxpweb/nl/${label}/customers/${customerId}/accounts/${accountId}/advancepayment`,
      body: requestBody,
      errors: { 400: 'Bad Request' },
    },
    requestConfig,
  );
}

type GetAdvancePaymentBe = { label: DC_Repositories_Base_Enumerations_Label; customerId: number; accountId: number };
/**
 * Get the advance payment amount details for BE.
 *
 * @returns ResponseModels_Financials_AdvancePaymentResponseModelBe Success
 */
export function getAdvancePaymentBe(
  { label, customerId, accountId }: GetAdvancePaymentBe,
  requestConfig: ApiRequestConfig = {},
): Promise<ResponseModels_Financials_AdvancePaymentResponseModelBe> {
  return request(
    {
      method: 'GET',
      path: `/dxpweb/be/${label}/customers/${customerId}/accounts/${accountId}/advancepayment`,
      errors: { 400: 'Bad Request' },
    },
    requestConfig,
  );
}

type PutDesiredAdvancePayment = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  orderId: string;
  requestBody?: RequestModels_Financials_PutDesiredAdvancePaymentRequest;
};
/**
 * Update desired advance payment amount for an open order
 *
 * @returns any Success
 */
export function putDesiredAdvancePayment(
  { businessUnit, label, customerId, orderId, requestBody }: PutDesiredAdvancePayment,
  requestConfig: ApiRequestConfig = {},
): Promise<any> {
  return request(
    {
      method: 'PUT',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/orders/${orderId}/desiredadvancepayment`,
      body: requestBody,
      errors: { 400: 'Bad Request' },
    },
    requestConfig,
  );
}

type GetFinancialOverview = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  accountId: number;
};
/**
 * Get the financial overview.
 *
 * @returns ResponseModels_Financials_FinancialOverviewResponse Success
 */
export function getFinancialOverview(
  { businessUnit, label, customerId, accountId }: GetFinancialOverview,
  requestConfig: ApiRequestConfig = {},
): Promise<ResponseModels_Financials_FinancialOverviewResponse> {
  return request(
    {
      method: 'GET',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/accounts/${accountId}/financialoverview`,
      errors: { 400: 'Bad Request' },
    },
    requestConfig,
  );
}

type GetFinancialPreferences = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  accountId: number;
};
/**
 * Get the financial preferences.
 *
 * @returns ResponseModels_Financials_FinancialPreferencesResponse Success
 */
export function getFinancialPreferences(
  { businessUnit, label, customerId, accountId }: GetFinancialPreferences,
  requestConfig: ApiRequestConfig = {},
): Promise<ResponseModels_Financials_FinancialPreferencesResponse> {
  return request(
    {
      method: 'GET',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/accounts/${accountId}/financialpreferences`,
      errors: { 400: 'Bad Request' },
    },
    requestConfig,
  );
}

type UpdateFinancialPreferences = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  accountId: number;
  requestBody?: RequestModels_Financials_FinancialPreferencesRequest;
};
/**
 * Updates the financial preferences.
 *
 * @returns any Success
 */
export function updateFinancialPreferences(
  { businessUnit, label, customerId, accountId, requestBody }: UpdateFinancialPreferences,
  requestConfig: ApiRequestConfig = {},
): Promise<any> {
  return request(
    {
      method: 'PUT',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/accounts/${accountId}/financialpreferences`,
      body: requestBody,
      errors: { 400: 'Bad Request' },
    },
    requestConfig,
  );
}

type DownloadInvoiceDocument = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  accountId: number;
  invoiceId: number;
};
/**
 * Gets the invoice pdf file in a filestream given a customer id, account id and invoice id.
 *
 * @returns Blob If process went ok, body contains an object with document metadata + the actual file in a base64 string.
 */
export function downloadInvoiceDocument(
  { businessUnit, label, customerId, accountId, invoiceId }: DownloadInvoiceDocument,
  requestConfig: ApiRequestConfig = {},
): Promise<Blob> {
  return request(
    {
      method: 'GET',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/accounts/${accountId}/invoices/${invoiceId}`,
      errors: {
        400: 'If a functional error occurs or the input is invalid.',
        403: 'If there is no invoice for this customer with this invoiceId',
      },
    },
    requestConfig,
  );
}

type GetPaymentArrangement = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  accountId: number;
};
/**
 * Gets the payment arrangement.
 *
 * @returns ResponseModels_Financials_PaymentArrangement_PaymentArrangementModel Success
 */
export function getPaymentArrangement(
  { businessUnit, label, customerId, accountId }: GetPaymentArrangement,
  requestConfig: ApiRequestConfig = {},
): Promise<ResponseModels_Financials_PaymentArrangement_PaymentArrangementModel> {
  return request(
    {
      method: 'GET',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/accounts/${accountId}/paymentarrangement`,
      errors: { 400: 'Bad Request', 404: 'Not Found' },
    },
    requestConfig,
  );
}

type CreatePaymentArrangement = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  accountId: number;
  requestBody: RequestModels_Financials_PaymentArrangementsRequest;
};
/**
 * Creates the payment arrangement.
 *
 * @returns ResponseModels_Financials_PaymentArrangement_PaymentArrangementResponseModel Success
 */
export function createPaymentArrangement(
  { businessUnit, label, customerId, accountId, requestBody }: CreatePaymentArrangement,
  requestConfig: ApiRequestConfig = {},
): Promise<ResponseModels_Financials_PaymentArrangement_PaymentArrangementResponseModel> {
  return request(
    {
      method: 'PUT',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/accounts/${accountId}/paymentarrangement`,
      body: requestBody,
      errors: { 400: 'Bad Request' },
    },
    requestConfig,
  );
}

type ProposePaymentArrangement = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  accountId: number;
  requestBody: RequestModels_Financials_PaymentArrangementsRequest;
};
/**
 * Proposes the payment arrangement.
 *
 * @returns ResponseModels_Financials_PaymentArrangement_PaymentArrangementResponseModel Success
 */
export function proposePaymentArrangement(
  { businessUnit, label, customerId, accountId, requestBody }: ProposePaymentArrangement,
  requestConfig: ApiRequestConfig = {},
): Promise<ResponseModels_Financials_PaymentArrangement_PaymentArrangementResponseModel> {
  return request(
    {
      method: 'POST',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/accounts/${accountId}/paymentarrangement/proposal`,
      body: requestBody,
      errors: { 400: 'Bad Request' },
    },
    requestConfig,
  );
}

type GetAdvancePaymentAdvice = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  accountId: number;
  generateAdvice?: boolean;
};
/**
 * Get the advance payment advice for a customer on one of his accounts.
 *
 * @returns ResponseModels_Financials_AdvancePaymentAdvice Success
 * @deprecated
 */
export function getAdvancePaymentAdvice(
  { businessUnit, label, customerId, accountId, generateAdvice }: GetAdvancePaymentAdvice,
  requestConfig: ApiRequestConfig = {},
): Promise<ResponseModels_Financials_AdvancePaymentAdvice> {
  return request(
    {
      method: 'GET',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/accounts/${accountId}/advancepayment/advice`,
      query: { generateAdvice },
      errors: { 400: 'Bad Request' },
    },
    requestConfig,
  );
}

type GetAdvancePaymentAdviceV2 = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  accountId: number;
};
/**
 * Get the advance payment advice for a customer on one of his accounts.
 *
 * @returns ResponseModels_Financials_AdvancePaymentAdviceV2_AdvancePaymentAdviceV2 Success
 */
export function getAdvancePaymentAdviceV2(
  { businessUnit, label, customerId, accountId }: GetAdvancePaymentAdviceV2,
  requestConfig: ApiRequestConfig = {},
): Promise<ResponseModels_Financials_AdvancePaymentAdviceV2_AdvancePaymentAdviceV2> {
  return request(
    {
      method: 'GET',
      path: `/dxpweb/v2/${businessUnit}/${label}/customers/${customerId}/accounts/${accountId}/advancepayment/advice`,
      errors: { 400: 'Bad Request' },
    },
    requestConfig,
  );
}

type GetInvoicesOverview = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  accountId: number;
  dateFrom?: string;
  year?: number;
};
/**
 * Get the invoices overview for a customer on one of his accounts.
 *
 * @returns ResponseModels_Financials_Invoices_InvoiceOverviewResponseModel Success
 */
export function getInvoicesOverview(
  { businessUnit, label, customerId, accountId, dateFrom, year }: GetInvoicesOverview,
  requestConfig: ApiRequestConfig = {},
): Promise<ResponseModels_Financials_Invoices_InvoiceOverviewResponseModel> {
  return request(
    {
      method: 'GET',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/accounts/${accountId}/invoices`,
      query: { dateFrom, year },
      errors: { 400: 'Bad Request' },
    },
    requestConfig,
  );
}

type GetPaymentPlanBreakdown = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  accountId: number;
};
/**
 * Get the payment plan breakdown for a customer.
 *
 * @returns ResponseDataDC_Domain_Models_Accounts_PaymentPlanBreakdown_PaymentPlanBreakdown Success
 */
export function getPaymentPlanBreakdown(
  { businessUnit, label, customerId, accountId }: GetPaymentPlanBreakdown,
  requestConfig: ApiRequestConfig = {},
): Promise<ResponseDataDC_Domain_Models_Accounts_PaymentPlanBreakdown_PaymentPlanBreakdown> {
  return request(
    {
      method: 'GET',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/accounts/${accountId}/paymentplan/breakdown`,
      errors: { 400: 'Bad Request' },
    },
    requestConfig,
  );
}
